import type { CreateDuplicateInput } from '@repo/api'
import type { GetLoadDuplicateTemplate } from '@repo/schema/load-duplicate'
import { toMinorUnits } from '@repo/sdk/helpers'

import type { DuplicateCardValues } from './duplicate-card.ts'

/** The permissions the API requires before it accepts these details filled in. */
export type DuplicateAccess = {
  /** `loads:assignees:manage` */
  canManageAssignees: boolean
  /** `loads:customs:manage` */
  canManageCustoms: boolean
  /** `loads:estimated-costs:manage` */
  canManageEstimatedCosts: boolean
}

const toCustomerRate = (
  { customer_rate_amount, customer_rate_currency }: DuplicateCardValues,
  template: GetLoadDuplicateTemplate,
) => {
  const amount = toMinorUnits(customer_rate_amount)
  // An empty rate sends none, and the API saves the Load with a rate of 0.
  if (amount === null) return null

  return {
    amount,
    currency: customer_rate_currency,
    description: template.customer_rate?.description ?? null,
  }
}

/**
 * The `POST /loads/:loadId/duplicate` body. It keeps what the template copies from the Original
 * Load, takes each Stop's date, time and reference and the customer rate from the card, and resets
 * the carrier side, the carrier rate and payment terms, and the tags. Customs, estimated costs and
 * assignees go empty without their permission, because the API refuses the whole save otherwise.
 * An Original Load without an owner sends none.
 */
export const buildDuplicateBody = (
  template: GetLoadDuplicateTemplate,
  values: DuplicateCardValues,
  access: DuplicateAccess,
): CreateDuplicateInput => {
  const { source_load_id: _originalLoadId, ...copied } = template

  return {
    ...copied,
    assignee_ids: access.canManageAssignees ? copied.assignee_ids : [],
    carrier_payment_terms: null,
    carrier_rate: null,
    customer_rate: toCustomerRate(values, template),
    customs: access.canManageCustoms ? copied.customs : null,
    drops: copied.drops.map((stop, index) => {
      const edit = values.drops[index]

      return {
        ...stop,
        date: edit?.date ?? null,
        reference_no: edit?.reference_no?.trim() || null,
        time: edit?.time ?? stop.time,
      }
    }),
    end_carrier_company_id: null,
    end_carrier_contact_id: null,
    estimated_costs: access.canManageEstimatedCosts ? copied.estimated_costs : [],
    haulier_company_id: null,
    haulier_contact_id: null,
    tags: [],
    user_id: copied.user_id ?? undefined,
  }
}
